import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GradeFlowShell } from './GradeFlowShell';
import { GradePipelineSteps } from './GradePipelineSteps';
import { GradeStepHeader } from './GradeStepHeader';
import { GradeSummaryStats } from './GradeSummaryStats';
import { GradeSyncOptionsCard } from './GradeSyncOptionsCard';
import { PageWrapper } from '../PageWrapper';
import type { GradedWork } from '../../types';

export type GradeSyncPreflightReceipt = {
  key: string;
  studentName: string;
  ok: boolean;
  error?: string;
  emailOk?: boolean;
  emailSkipped?: boolean;
  emailMissing?: boolean;
  drivePagesSaved?: number;
  driveSkipped?: boolean;
  classroomSkipped?: boolean;
};

export type GradeSyncPaywallSurface =
  | 'sync_preflight'
  | 'sync_preflight_retry'
  | 'sync_preflight_start_sync';

export type GradeSyncPreflightViewProps = {
  courseNameHeader?: string;
  selectedCourseName?: string | null;
  selectedAssignmentTitle?: string | null;
  isOnline: boolean;
  isDarkMode: boolean;
  setIsDarkMode: Dispatch<SetStateAction<boolean>>;
  syncStatus: 'idle' | 'ok' | 'error';
  onSyncClick: () => void;
  onBack: () => void;
  gradedWorks: GradedWork[];
  syncPreflightIndexes: number[] | null;
  syncReceipts: GradeSyncPreflightReceipt[];
  syncFailedKeys: Set<string>;
  isPaid: boolean;
  gradeSyncPostToClassroom: boolean;
  setGradeSyncPostToClassroom: Dispatch<SetStateAction<boolean>>;
  gradeSyncEmailStudents: boolean;
  setGradeSyncEmailStudents: Dispatch<SetStateAction<boolean>>;
  gradeSyncSaveToDrive: boolean;
  setGradeSyncSaveToDrive: Dispatch<SetStateAction<boolean>>;
  onNavigatePaywall: (surface: GradeSyncPaywallSurface) => void;
  /** Save offline intent + leave to audit (parent handles storage, analytics, phase). */
  onQueueOfflinePublish: (indexes: number[]) => void;
  onStartSyncOnline: (indexes: number[]) => void;
  onRetryFailedOnline: (indexes: number[]) => void;
};

export function GradeSyncPreflightView({
  courseNameHeader,
  selectedCourseName,
  selectedAssignmentTitle,
  isOnline,
  isDarkMode,
  setIsDarkMode,
  syncStatus,
  onSyncClick,
  onBack,
  gradedWorks,
  syncPreflightIndexes,
  syncReceipts,
  syncFailedKeys,
  isPaid,
  gradeSyncPostToClassroom,
  setGradeSyncPostToClassroom,
  gradeSyncEmailStudents,
  setGradeSyncEmailStudents,
  gradeSyncSaveToDrive,
  setGradeSyncSaveToDrive,
  onNavigatePaywall,
  onQueueOfflinePublish,
  onStartSyncOnline,
  onRetryFailedOnline,
}: GradeSyncPreflightViewProps): ReactElement {
  const { t } = useTranslation();

  const targetIndexes = useMemo(
    () =>
      syncPreflightIndexes && syncPreflightIndexes.length > 0
        ? syncPreflightIndexes
        : gradedWorks.map((_, i) => i),
    [syncPreflightIndexes, gradedWorks]
  );

  const works = useMemo(
    () => targetIndexes.map((i) => gradedWorks[i]).filter(Boolean),
    [targetIndexes, gradedWorks]
  );

  const gradesCount = works.length;
  const emailCount = gradeSyncEmailStudents ? works.filter((w) => !!w.studentEmail).length : 0;
  const missingEmailCount = gradeSyncEmailStudents
    ? works.filter((w) => !w.studentEmail).length
    : 0;
  const drivePages = gradeSyncSaveToDrive
    ? works.reduce((sum, w) => sum + (w.imageUrls?.length || 0), 0)
    : 0;
  const classroomCount = gradeSyncPostToClassroom ? gradesCount : 0;

  const failedIndexes = useMemo(
    () =>
      gradedWorks
        .map((w, i) => ({ w, i }))
        .filter(({ w }) =>
          syncFailedKeys.has(`${w.courseId}::${w.assignmentId}::${w.studentId}::${w.timestamp}`)
        )
        .map(({ i }) => i),
    [gradedWorks, syncFailedKeys]
  );

  const syncDescClassroom = gradeSyncPostToClassroom
    ? t('gradeSync.preflight.classroomOn')
    : t('gradeSync.preflight.classroomOff');
  const syncDescEmail = gradeSyncEmailStudents
    ? t('gradeSync.preflight.emailOn')
    : t('gradeSync.preflight.emailOff');
  const syncDescDrive = gradeSyncSaveToDrive
    ? t('gradeSync.preflight.driveOn')
    : t('gradeSync.preflight.driveOff');

  return (
    <PageWrapper
      headerTitle={t('gradeSync.preflight.headerTitle')}
      headerSubtitle={courseNameHeader || undefined}
      onBack={onBack}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={onSyncClick}
    >
      <GradeFlowShell
        step={t('gradeSync.preflight.stepLabel')}
        title={t('gradeSync.preflight.title')}
        subtitle={t('gradeSync.preflight.subtitle')}
      >
        <GradeStepHeader
          label={t('gradeSync.preflight.batchCheckLabel')}
          title={t('gradeSync.preflight.reviewTitle')}
          courseName={selectedCourseName ?? undefined}
          assignmentTitle={selectedAssignmentTitle ?? undefined}
        />
        <GradePipelineSteps activeIndex={3} className="mb-1" />
        {!isPaid && (
          <div
            className="p-4 rounded-2xl bg-rose-500/10 dark:bg-rose-500/10 border border-rose-400/40 dark:border-rose-500/35"
            role="status"
          >
            <p className="text-sm font-black text-rose-900 dark:text-rose-100">
              {t('gradeSync.preflight.paywallTitle')}
            </p>
            <p className="mt-1 text-[12px] font-semibold text-rose-800/95 dark:text-rose-100/90 leading-snug">
              {t('gradeSync.preflight.paywallBody')}
            </p>
            <button
              type="button"
              onClick={() => onNavigatePaywall('sync_preflight')}
              className="mt-3 w-full py-3 rounded-xl bg-rose-600 text-white text-[12px] font-black uppercase tracking-widest shadow-sm hover:bg-rose-700 transition-colors"
            >
              {t('gradeSync.preflight.unlockSync')}
            </button>
          </div>
        )}
        <GradeSyncOptionsCard
          options={[
            {
              id: 'classroom',
              label: t('gradeSync.preflight.postToClassroom'),
              value: gradeSyncPostToClassroom,
              onToggle: () => setGradeSyncPostToClassroom((v) => !v),
              title: t('gradeSync.preflight.postToClassroomTitle'),
            },
            {
              id: 'email',
              label: t('gradeSync.preflight.emailStudents'),
              value: gradeSyncEmailStudents,
              onToggle: () => setGradeSyncEmailStudents((v) => !v),
              title: t('gradeSync.preflight.emailStudentsTitle'),
            },
            {
              id: 'drive',
              label: t('gradeSync.preflight.saveToDrive'),
              value: gradeSyncSaveToDrive,
              onToggle: () => setGradeSyncSaveToDrive((v) => !v),
              title: t('gradeSync.preflight.saveToDriveTitle'),
            },
          ]}
          description={t('gradeSync.preflight.syncDescription', {
            classroom: syncDescClassroom,
            email: syncDescEmail,
            drive: syncDescDrive,
          })}
        />

        {!isOnline && (
          <p
            className="text-[11px] font-semibold text-amber-800 dark:text-amber-100/95 bg-amber-500/10 border border-amber-400/35 rounded-xl px-3 py-2"
            role="status"
          >
            {t('gradeSync.preflightOfflineHint')}
          </p>
        )}

        <GradeSummaryStats
          title={t('gradeSync.preflight.impactTitle')}
          stats={[
            {
              label: t('gradeSync.preflight.statClassroom'),
              value: classroomCount,
              helper: !gradeSyncPostToClassroom ? t('gradeSync.preflight.skipped') : undefined,
            },
            {
              label: t('gradeSync.preflight.statEmail'),
              value: emailCount,
              helper: !gradeSyncEmailStudents
                ? t('gradeSync.preflight.skipped')
                : missingEmailCount > 0
                  ? t('gradeSync.preflight.missingEmail', { count: missingEmailCount })
                  : undefined,
            },
            {
              label: t('gradeSync.preflight.statDrive'),
              value: drivePages,
              helper: !gradeSyncSaveToDrive ? t('gradeSync.preflight.skipped') : undefined,
            },
          ]}
        />

        {syncReceipts.length > 0 && (
          <div className="p-4 rounded-2xl bg-white/70 dark:bg-slate-800/55 border border-slate-200/70 dark:border-slate-700/60 shadow-sm overflow-hidden">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {t('gradeSync.preflight.lastReceipt')}
            </div>
            <div className="mt-3 max-h-[220px] overflow-y-auto custom-scrollbar space-y-2">
              {syncReceipts.map((r) => (
                <div
                  key={r.key}
                  className={`p-3 rounded-xl border flex items-center justify-between gap-3 ${
                    r.ok
                      ? 'bg-emerald-500/5 border-emerald-500/20'
                      : 'bg-rose-500/5 border-rose-500/20'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="font-black text-sm text-slate-900 dark:text-white truncate">
                      {r.studentName}
                    </div>
                    {!r.ok && r.error && (
                      <div className="text-[11px] text-rose-600 dark:text-rose-300 truncate">
                        {r.error}
                      </div>
                    )}
                    {r.ok && (
                      <div className="text-[11px] text-slate-600 dark:text-slate-300">
                        {t('gradeSync.preflight.driveLine')}{' '}
                        {r.driveSkipped
                          ? t('gradeSync.preflight.skipped')
                          : t('gradeSync.preflight.pages', {
                              count: r.drivePagesSaved ?? 0,
                            })}{' '}
                        · {t('gradeSync.preflight.emailLine')}{' '}
                        {r.emailSkipped
                          ? t('gradeSync.preflight.skipped')
                          : r.emailMissing
                            ? t('gradeSync.preflight.emailMissing')
                            : r.emailOk === undefined
                              ? t('gradeSync.preflight.emailNa')
                              : r.emailOk
                                ? t('gradeSync.preflight.emailSent')
                                : t('gradeSync.preflight.emailFailed')}
                      </div>
                    )}
                  </div>
                  <div
                    className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shrink-0 ${
                      r.ok
                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                        : 'bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-300'
                    }`}
                  >
                    {r.ok
                      ? t('gradeSync.preflight.statusOk')
                      : t('gradeSync.preflight.statusFailed')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-auto grid grid-cols-1 gap-2">
          {failedIndexes.length > 0 && (
            <button
              type="button"
              onClick={() => {
                if (!isPaid) {
                  onNavigatePaywall('sync_preflight_retry');
                  return;
                }
                if (!isOnline) {
                  onQueueOfflinePublish(failedIndexes);
                  return;
                }
                onRetryFailedOnline(failedIndexes);
              }}
              className="py-4 px-5 rounded-2xl bg-amber-500 text-white font-black uppercase tracking-widest text-[14px] shadow-sm hover:bg-amber-600 transition-colors"
            >
              {t('gradeSync.preflight.retryFailed', { count: failedIndexes.length })}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (!isPaid) {
                onNavigatePaywall('sync_preflight_start_sync');
                return;
              }
              if (!isOnline) {
                onQueueOfflinePublish(targetIndexes);
                return;
              }
              onStartSyncOnline(targetIndexes);
            }}
            disabled={
              gradesCount === 0 ||
              (!gradeSyncPostToClassroom && !gradeSyncEmailStudents && !gradeSyncSaveToDrive)
            }
            className="py-4 px-5 rounded-2xl bg-emerald-500 text-white font-black uppercase tracking-widest text-[14px] shadow-sm hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPaid
              ? isOnline
                ? t('gradeSync.preflight.startSync')
                : t('gradeSync.preflight.saveForLater')
              : t('gradeSync.preflight.startSyncPro')}
          </button>
        </div>
      </GradeFlowShell>
    </PageWrapper>
  );
}
